import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthCallback, AuthGate } from '@aosenuma/lib-supabase';
import { bgPlaneta, bgMontanas } from '@aosenuma/ui';
import { Landing } from '@/pages/Landing';
import { AvisoPrivacidad } from '@/pages/AvisoPrivacidad';
import { SLA } from '@/pages/SLA';

// Lazy: el embed del SIG arrastra mapbox-gl (~1.8MB). Visitantes que solo abren
// la home no deben pagar ese chunk.
const SigLiveEmbed = lazy(() =>
  import('@/pages/SigLiveEmbed').then((m) => ({ default: m.SigLiveEmbed })),
);

const URL_DASHBOARD = import.meta.env.VITE_PUBLIC_URL_DASHBOARD ?? 'http://localhost:5174';
const URL_EQUIPO = import.meta.env.VITE_PUBLIC_URL_EQUIPO ?? 'http://localhost:5175';

// Fail-fast: en prod, falta de env = redirección silenciosa a localhost. Mejor
// tronar el bundle al cargar que mandar usuarios reales a una URL inalcanzable.
if (import.meta.env.PROD) {
  if (!import.meta.env.VITE_PUBLIC_URL_DASHBOARD) {
    throw new Error('VITE_PUBLIC_URL_DASHBOARD requerido en producción');
  }
  if (!import.meta.env.VITE_PUBLIC_URL_EQUIPO) {
    throw new Error('VITE_PUBLIC_URL_EQUIPO requerido en producción');
  }
}

const cssVars = {
  ['--bg-hero' as never]: `url(${bgPlaneta})`,
  ['--bg-footer' as never]: `url(${bgMontanas})`,
} satisfies React.CSSProperties;

/** Resuelve subdominio destino por rol_app del JWT. */
function destByRol(rolApp: string | null): string {
  if (rolApp === 'cliente_elawan' || rolApp === 'auditor') return URL_DASHBOARD;
  return URL_EQUIPO; // admin/tecnico Aosenuma
}

/**
 * Si Supabase manda al root con `?code=` (PKCE) o `#access_token=` (implicit),
 * procesamos el callback antes de mostrar la landing. Sin esto, el code se
 * pierde si el user llega aquí en vez de /auth/callback (puede pasar cuando
 * el redirect_to del magic link no está en el allow list de Supabase y cae
 * al Site URL como fallback).
 */
function RootRouter() {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const hasCode = url.searchParams.has('code');
    const hasAccessToken = window.location.hash.includes('access_token=');
    if (hasCode || hasAccessToken) {
      return <AuthCallback redirectTo={destByRol} />;
    }
  }
  return <Landing />;
}

export function App() {
  return (
    <div className="app-landing" style={cssVars}>
      <Routes>
        <Route path="/" element={<RootRouter />} />
        {/* Visor compartible — gate por auth (acta + threat A/B):
            antes era publico sin auth, lo que permitia scraping de los
            210 poligonos + props (area, nucleo, municipio). Ahora solo
            staff Aosenuma/Elawan logueado. Si no hay sesion, AuthGate
            redirige a "/" (mismo origin) donde el form de login espera. */}
        <Route
          path="/siglive"
          element={
            <AuthGate
              allowed={['admin_aosenuma', 'tecnico_aosenuma', 'cliente_elawan', 'auditor']}
              unauthenticated="/"
            >
              <Suspense fallback={<div style={{ minHeight: '100dvh', background: '#0a1118' }} />}>
                <SigLiveEmbed />
              </Suspense>
            </AuthGate>
          }
        />
        <Route path="/aviso-privacidad" element={<AvisoPrivacidad />} />
        <Route path="/sla" element={<SLA />} />
        {/* Alias UPPERCASE por si algún link externo usa la forma original
            (Diego escribió `/SLA` en la spec). React Router es
            case-sensitive y registramos ambos. */}
        <Route path="/SLA" element={<SLA />} />
        <Route path="/auth/callback" element={<AuthCallback redirectTo={destByRol} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
